/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable max-len */
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IMaskInput } from 'react-imask';
import styled from 'styled-components';
import { history } from '../../configureStore';
import ContainerSkeleton from '../skeleton/ContainerSkeleton';
import merge from 'lodash.merge';
import moment from 'moment';
import { selectVariableData, selectLocalVariableData } from '../../store/page/selectors';
import { formValuesRequest, submitFormRequest, mergeStyleRequest, pageRequest, updateVariableRequest, actionRequest } from '../../store/page/actions';
import { combineClassNames, replacePlaceholdersWithVariablesV2, handleOpenUrl, getSizesBasedOnScreen, executeFunction, getNestedValue, isClickInsidePortal } from '../../utils/base';
import {
  TextInput, TextAreaInput, PasswordInput, EmailInput, NumberInput, FileInput, PhoneInput, DateTimePickerInput,
  StyleUpdateChunk, RequestUpdateChunk, FunctionUpdateChunk,
  Breakpoints,
  ActionChunk,
} from '../../types/base';
import DateRangePicker from '../DateRangePicker';
import generateComponentStyles from '../../utils/styleHelper';
import { getVariableValue, setVariableValue } from '../../utils/globalVariableHelper';

const validateInputPattern = (value: string, pattern?: string, patternFlag?: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  if (!pattern) return true;
  
  try {
    const regex = patternFlag 
      ? new RegExp(pattern, patternFlag)
      : new RegExp(pattern);
      
    return regex.test(value);
  } catch (e) {
    console.error('Invalid regex pattern or flag:', { pattern, patternFlag, error: e });
    return false; // Fail closed for invalid patterns
  }
};

const StyledInput = styled.input`
    z-index: 1;
  ${(props) => generateComponentStyles(props.$shape, props.$baseScreenResolution, props.$skipStates)}
`;

// trigger

const StyledTextarea = styled.textarea`
  resize: none;
  z-index: 1;
  ${(props) => generateComponentStyles(props.$shape, props.$baseScreenResolution, props.$skipStates)}
`;

interface TextInputType extends TextInput {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  pageSettings?: any;
  parentLoading?: boolean;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

interface PasswordInputType extends PasswordInput {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  pageSettings?: any;
  parentLoading?: boolean;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

interface NumberInputType extends NumberInput {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  pageSettings?: any;
  parentLoading?: boolean;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

interface TextAreaInputType extends TextAreaInput {
  dataLake?: any;
  nestedTable?: boolean;
  parentIndex?: number;
  styleChunks?: any;
  pageSettings?: any;
  parentLoading?: boolean;
  enableLocalShimmer?: boolean;
  valueOriginal?: string;
  cache?: boolean;
}

const Input: React.FC<TextInputType | PasswordInputType  | NumberInputType> = ({
  id, style: stDraft, placeholder,
  value: valueDraft,
  tooltip,
  events: eventsDraft,
  soc,
  pageSettings,
  dataLake,
  activeState: activeStateDraft,
  regexp,
  settings,
  type,
  nestedTable,
  parentIndex,
  styleChunks,
  skeleton: renderSkeleton2,
  parentLoading,
  enableLocalShimmer,
  valueOriginal,
  cache,
}) => {
  const { ref } = settings || {};
  const launchExecutedRef = useRef<undefined | null | string>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const variableData = useSelector(selectVariableData);
  const localVariableData = useSelector(selectLocalVariableData);
  const { delay = 500, autocomplete } = settings || {};
  const { style: styleDraft, events, activeState, ref: realRef, value } = dataLake?.data ? replacePlaceholdersWithVariablesV2({ value: valueDraft, ref, style: stDraft, events: eventsDraft, activeState: activeStateDraft }, dataLake.data) : { value: valueDraft, ref: undefined, style: stDraft, events: eventsDraft, activeState: activeStateDraft };
const mergeStylesWithChunks = useCallback((style: any, chunks: any, index: number) => {
  const { indexes, refs } = chunks || {};
  let mergedStyle = style;
  
  // Apply index-based styles first
  if (indexes && indexes[id] && indexes[id][index] && typeof index === 'number') {
    mergedStyle = merge({}, mergedStyle, indexes[id][index]);
  }
  
  // Apply ref-based updates at the end
  if (refs && realRef) {
    const refUpdate = refs[realRef];
    if (refUpdate?.type && refUpdate.type === 'input') {
      mergedStyle = merge({}, mergedStyle, refUpdate?.update);
    }
  }
  
  return mergedStyle;
}, [id, realRef]);

  const renderSceleton = useMemo(() => {
    if (enableLocalShimmer) {
      if (cache === false) {
        return false;
      }
      return (activeState === 'loading' || parentLoading) ? (cache || (typeof valueOriginal === 'string' && /{{.*}}/.test(valueOriginal))) ? true : false : false;
    }
    return renderSkeleton2;
  }, [renderSkeleton2, activeState, enableLocalShimmer, valueOriginal, parentLoading]);

  // First useMemo to merge active state styles with base style
  const mergedStyleWithState = useMemo(() => {
    let mergedStyle = styleDraft;
    if (activeState && styleDraft?.states?.[activeState]) {
      mergedStyle = merge({}, styleDraft, styleDraft.states[activeState]);
    }

    // Handle states in breakpoints
    if (styleDraft?.breakpoints) {
      mergedStyle.breakpoints = Object.entries(styleDraft.breakpoints).reduce((acc, [breakpoint, breakpointStyle]) => {
        const typedBreakpointStyle = breakpointStyle as Breakpoints;
        if (typedBreakpointStyle?.states?.[activeState]) {
          acc[breakpoint] = merge({}, typedBreakpointStyle, typedBreakpointStyle.states[activeState]);
        } else {
          acc[breakpoint] = typedBreakpointStyle;
        }
        return acc;
      }, {} as typeof styleDraft.breakpoints);
    }

    return mergedStyle;
  }, [styleDraft, activeState]);

  // Second useMemo to merge styles with chunks based on parentIndex
  const style = useMemo(() => {
    return mergeStylesWithChunks(mergedStyleWithState, styleChunks, parentIndex);
  }, [mergedStyleWithState, styleChunks, parentIndex]);
  const baseScreenResolution =  pageSettings?.baseScreenResolution ? getSizesBasedOnScreen(pageSettings?.baseScreenResolution) : undefined;
  const [internalValue, setInternalValue] = useState<string>(value);
  const { autofocus } = style || {};
  const dispatch = useDispatch();

  const internalFns = {
    $setVariable: (key: string, value: any) => {
      setVariableValue(key, value, (localKey, localValue) => {
        dispatch(updateVariableRequest({ variable: localKey, value: localValue }));
      });
    },
    $getVariable: (key: string) => {
      return getVariableValue(key, variableData, localVariableData);
    },
    $moment: (...args: any[]) => moment(...args)
  };

  const handleActionChunk = (actionUpdates: ActionChunk[], filterLoadingState?: StyleUpdateChunk[]) => {
    actionUpdates.forEach((update) => {
      dispatch(actionRequest({ 
        actionUpdate: update, 
        componentId: id 
      }, filterLoadingState));
    });
  };

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const { 
    on_focus: focusStyles, 
    on_change: changeStyles, 
    on_blur: blurStyles, 
    on_launch: launchUpdates,
    on_hover: hoverUpdates,
    on_unhover: unhoverUpdates,
    on_regexp_success: regexpSuccessUpdates,
    on_regexp_error: regexpErrorUpdates,
    on_outclick: outclickUpdates,
  } = events || {};

  useEffect(() => {
    if (type === 'number' && typeof value === 'string') {
    setInternalValue(value.replace(/,/g, '.'));
  } else {
    setInternalValue(value);
  }
    }, [type, value]);

  useEffect(() => {
    if (type === 'number' && typeof value === 'string') {
      if (value !== internalValue) {
        setInternalValue(value.replace(/,/g, '.'));
      }
    } else {
      if (value !== internalValue) {
        setInternalValue(value);
      }
    }
  }, [type, value]);

  // const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
  //   // Если input в фокусе — снимаем фокус, чтобы не менялось значение
  //   if (type === 'number' && document.activeElement === inputRef.current) {
  //     inputRef.current?.blur();
  //     setTimeout(() => {
  //       inputRef.current?.focus();
  //     }, 0);
  //   }
  // };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (activeState === 'disabled' || activeState === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    dispatch(submitFormRequest({ block: true }));

    let newValue = e.target.value;

      // If type is number, allow only digits and one decimal separator
  if (type === 'number') {
    // Replace comma with dot
    newValue = newValue.replace(/,/g, '.');
    // Remove invalid characters (allow digits and one dot)
    newValue = newValue.replace(/[^0-9.]/g, '');
    // Only one dot allowed
    const parts = newValue.split('.');
    if (parts.length > 2) {
      newValue = parts[0] + '.' + parts.slice(1).join('');
    }
  }

        // Check max length
    if (regexp?.max_length && newValue.length > regexp.max_length) {
      return;
    }
    setInternalValue(newValue);
    dispatch(formValuesRequest({ field: { id: realRef || id, value: newValue } }));
    // Check pattern validation
    if (changeStyles) {
      const styleUpdates = changeStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = changeStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = changeStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = changeStyles.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
        handleActionChunk(actionUpdates, filterLoadingState);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({ 
              location: { pathname: value }, 
              silent,
              componentId: id, 
            },
          filterLoadingState
          ));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }, filterLoadingState));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
    if (regexp?.pattern && typeof regexp.pattern === 'string') {
      const result = validateInputPattern(newValue, regexp.pattern);
      if (regexpErrorUpdates && !result) {
          const styleUpdates = regexpErrorUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
          const requestUpdates = regexpErrorUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
          const functionUpdates = regexpErrorUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
          const actionUpdates = regexpErrorUpdates.filter((update): update is ActionChunk => update && 'action' in update);
          if (functionUpdates.length) {
            functionUpdates.forEach((update) => {
              if (update?.function) {
                executeFunction(update.function, e, internalFns);
              }
            });
          }
          if (styleUpdates.length) {
            dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
          }
          if (actionUpdates.length) {
            handleActionChunk(actionUpdates);
          }
          if (requestUpdates.length) {
            requestUpdates.forEach((update) => {
              const { method, value, silent, config } = update?.request || {};
              const { delay } = config || {};
              if (update?.request && method === 'command') {
                dispatch(pageRequest({ 
                  location: { pathname: value }, 
                  silent,
                  componentId: id,
                }));
              } else if (update?.request && method === 'action') {
                dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
              } else if (update?.request && method === 'open-url') {
                handleOpenUrl(value, delay, silent, history);
              }
            });
          }
      } else if (regexpSuccessUpdates && result) {
          const styleUpdates = regexpSuccessUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
          const requestUpdates = regexpSuccessUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
          const functionUpdates = regexpSuccessUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
          const actionUpdates = regexpSuccessUpdates.filter((update): update is ActionChunk => update && 'action' in update);
          if (functionUpdates.length) {
            functionUpdates.forEach((update) => {
              if (update?.function) {
                executeFunction(update.function, e, internalFns);
              }
            });
          }
          if (styleUpdates.length) {
            dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
          }
          if (actionUpdates.length) {
            const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
            handleActionChunk(actionUpdates, filterLoadingState);
          }
          if (requestUpdates.length) {
            requestUpdates.forEach((update) => {
              const { method, value, silent, config } = update?.request || {};
              const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
              const { delay } = config || {};
              if (update?.request && method === 'command') {
                dispatch(pageRequest({ 
                  location: { pathname: value }, 
                  silent,
                  componentId: id, 
                }, filterLoadingState));
              } else if (update?.request && method === 'action') {
                dispatch(submitFormRequest({ value, silent, delay, componentId: id }, filterLoadingState));
              } else if (update?.request && method === 'open-url') {
                handleOpenUrl(value, delay, silent, history);
              }
            });
          }
      }
    }
    if (soc) {
      dispatch(submitFormRequest({ value: 'soc', delay, componentId: id }));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (activeState === 'disabled' || activeState === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  }

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (activeState === 'disabled' || activeState === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (focusStyles) {
      const styleUpdates = focusStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = focusStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = focusStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = focusStyles.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({ 
              location: { pathname: value },
               silent,
              componentId: id, 
              }));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest(
              { value, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (activeState === 'disabled' || activeState === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (blurStyles) {
      const styleUpdates = blurStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = blurStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = blurStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = blurStyles.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({ 
              location: { pathname: value }, 
              silent,
              componentId: id,
            }));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  };

  const handleHover = (e: React.MouseEvent<HTMLInputElement>) => {
    if (hoverUpdates) {
      const styleUpdates = hoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = hoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = hoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = hoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  
  }

  const handleUnhover = (e: React.MouseEvent<HTMLInputElement>) => {
    if (unhoverUpdates) {
      const styleUpdates = unhoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = unhoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = unhoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = unhoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  }

  useEffect(() => {
    if (launchExecutedRef.current !== style?.visibility && launchUpdates && launchUpdates.length) {
      launchExecutedRef.current = style?.visibility; // Set the flag to true
      const requestUpdates = launchUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = launchUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = launchUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, undefined, internalFns);
          }
        });
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            // const newTimeout = setTimeout(() => {
            //   dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
            // }, config?.delay * 1000 || 0);

            // timeoutsRef.current.push(newTimeout);
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }

    }

    return () => {
      timeoutsRef?.current?.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, [launchUpdates, style?.visibility]);

  // Memoize the filtered updates to avoid recalculating on every click
  const filteredUpdates = useMemo(() => {
    if (!outclickUpdates) return null;
    
    return {
      styleUpdates: outclickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update),
      requestUpdates: outclickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update),
      functionUpdates: outclickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update),
      actionUpdates: outclickUpdates.filter((update): update is ActionChunk => update && 'action' in update)
    };
  }, [outclickUpdates]);

  useEffect(() => {
    if (!outclickUpdates?.length) return undefined;
    
    const handleUnclick = (e: MouseEvent) => {
      if (activeState === 'disabled' || activeState === 'loading') return;
      
      if (
        outclickUpdates?.length &&
        inputRef.current && 
        !inputRef.current.contains(e.target as Node) &&
        !isClickInsidePortal(e.target as Node) &&
        filteredUpdates
      ) {
        // Execute function updates
        if (filteredUpdates.functionUpdates.length) {
          filteredUpdates.functionUpdates.forEach((update) => {
            if (update?.function) {
              executeFunction(update.function, undefined, internalFns);
            }
          });
        }
        
        // Apply style updates
        if (filteredUpdates.styleUpdates.length) {
          dispatch(mergeStyleRequest(filteredUpdates.styleUpdates, parentIndex));
        }
        
        // Process action updates
        if (filteredUpdates.actionUpdates.length) {
          handleActionChunk(filteredUpdates.actionUpdates);
        }
        
        // Process request updates
        if (filteredUpdates.requestUpdates.length) {
          filteredUpdates.requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ 
                value, 
                silent, 
                delay, 
                componentId: id 
              }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      }
    };
    
    document.addEventListener('mousedown', handleUnclick);
    return () => {
      document.removeEventListener('mousedown', handleUnclick);
    };
  }, [
    parentIndex, 
    activeState, 
    id, 
    stDraft?.states?.loading,
    outclickUpdates?.length,
  ]);

  if (renderSceleton && style) {
    return <ContainerSkeleton 
      style={style}
      baseScreenResolution={baseScreenResolution}
      skipStates={activeState === 'disabled' || activeState === 'loading'}
    />;
  }

  return (
  // <Wrapper $shape={style}>
  // {activeBackground?.background && Array.isArray(activeBackground?.background) ? activeBackground.background.map((bg) => (
  //   <MainLayerController
  //     options={{ 
  //       borderRadius: activeBackground.borderRadius,
  //       borderWidth: activeBackground.borderWidth,
  //      }}
  //     key={uuidv4()}
  //     {...bg}
  //   />
  // )) : null}
// Inside your Input component, modify the return statement:
  regexp?.mask_pattern ? (
    <StyledInput
      as={IMaskInput}
      mask={regexp.mask_pattern}
      value={internalValue}
      // onWheel={handleWheel}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={hoverUpdates?.length ? handleHover : undefined}
      onMouseLeave={unhoverUpdates?.length ? handleUnhover : undefined}
      disabled={activeState === 'disabled'}
      inputRef={inputRef}
      placeholder={placeholder}
       data-id={realRef || id}
      id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
      type={type === 'number' ? 'text' : (type || 'text')}
      style={{ display: style?.visibility === 'hidden' ? 'none' : undefined }}
      autoFocus={autofocus}
      maxLength={regexp?.max_length}
      onClick={(e) => {
        e.stopPropagation();
      }}
      $shape={style}
      autoComplete={autocomplete === false ? 'off' : 'on'}
      $baseScreenResolution={baseScreenResolution}
      $skipStates={activeState === 'disabled' || activeState === 'loading'}
      data-component="input"
      className={combineClassNames(style.class)}
      title={tooltip}
      onInput={handleInput}
      lang="en"
    />
  ) : (
    <StyledInput
      ref={inputRef}
      style={{ display: style?.visibility === 'hidden' ? 'none' : undefined }}
      placeholder={placeholder}
       data-id={realRef || id}
      id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
      type={type === 'number' ? 'text' : (type || 'text')}
      // onWheel={handleWheel}
      $shape={style}
      $baseScreenResolution={baseScreenResolution}
      $skipStates={activeState === 'disabled' || activeState === 'loading'}
      disabled={activeState === 'disabled'}
      data-component="input"
      autoComplete={autocomplete === false ? 'off' : 'on'}
      className={combineClassNames(style.class)}
      value={internalValue}
      title={tooltip}
      autoFocus={autofocus}
      onInput={handleInput}
      onClick={(e) => e.stopPropagation()}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={hoverUpdates?.length ? handleHover : undefined}
      onMouseLeave={unhoverUpdates?.length ? handleUnhover : undefined}
      maxLength={regexp?.max_length}
      lang="en"
    />
  )
);
};

const TextArea: React.FC<TextAreaInputType> = ({
  id, style: stDraft, placeholder,
  value: valueDraft,
  tooltip,
  events: eventsDraft,
  soc,
  pageSettings,
  dataLake,
  activeState: activeStateDraft,
  regexp,
  settings,
  nestedTable,
  parentIndex,
  styleChunks,
  skeleton: renderSkeleton2,
  parentLoading,
  enableLocalShimmer,
  valueOriginal,
  cache,
}) => {
  const { ref } = settings || {};
  const launchExecutedRef = useRef<undefined | null | string>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { delay = 500 } = settings || {};
  const variableData = useSelector(selectVariableData);
  const localVariableData = useSelector(selectLocalVariableData);
  const { style: styleDraft, events, activeState, ref: realRef, value } = dataLake?.data ? replacePlaceholdersWithVariablesV2({ value: valueDraft, ref, style: stDraft, events: eventsDraft, activeState: activeStateDraft}, dataLake.data) : { value: valueDraft, ref: undefined, style: stDraft, events: eventsDraft, activeState: activeStateDraft };
const mergeStylesWithChunks = useCallback((style: any, chunks: any, index: number) => {
  const { indexes, refs } = chunks || {};
  let mergedStyle = style;
  
  // Apply index-based styles first
  if (indexes && indexes[id] && indexes[id][index] && typeof index === 'number') {
    mergedStyle = merge({}, mergedStyle, indexes[id][index]);
  }
  
  // Apply ref-based updates at the end
  if (refs && realRef) {
    const refUpdate = refs[realRef];
    if (refUpdate?.type && refUpdate.type === 'input') {
      mergedStyle = merge({}, mergedStyle, refUpdate?.update);
    }
  }
  
  return mergedStyle;
}, [id, realRef]);

  const renderSceleton = useMemo(() => {
    if (enableLocalShimmer) {
      if (cache === false) {
        return false;
      }
      return (activeState === 'loading' || parentLoading) ? (cache || (typeof valueOriginal === 'string' && /{{.*}}/.test(valueOriginal))) ? true : false : false;
    }
    return renderSkeleton2;
  }, [renderSkeleton2, activeState, enableLocalShimmer, valueOriginal, parentLoading]);

  // First useMemo to merge active state styles with base style
  const mergedStyleWithState = useMemo(() => {
    let mergedStyle = styleDraft;
    if (activeState && styleDraft?.states?.[activeState]) {
      mergedStyle = merge({}, styleDraft, styleDraft.states[activeState]);
    }

    // Handle states in breakpoints
    if (styleDraft?.breakpoints) {
      mergedStyle.breakpoints = Object.entries(styleDraft.breakpoints).reduce((acc, [breakpoint, breakpointStyle]) => {
        const typedBreakpointStyle = breakpointStyle as Breakpoints;
        if (typedBreakpointStyle?.states?.[activeState]) {
          acc[breakpoint] = merge({}, typedBreakpointStyle, typedBreakpointStyle.states[activeState]);
        } else {
          acc[breakpoint] = typedBreakpointStyle;
        }
        return acc;
      }, {} as typeof styleDraft.breakpoints);
    }

    return mergedStyle;
  }, [styleDraft, activeState]);

  // Second useMemo to merge styles with chunks based on parentIndex
  const style = useMemo(() => {
    return mergeStylesWithChunks(mergedStyleWithState, styleChunks, parentIndex);
  }, [mergedStyleWithState, styleChunks, parentIndex]);
  const baseScreenResolution =  pageSettings?.baseScreenResolution ? getSizesBasedOnScreen(pageSettings?.baseScreenResolution) : undefined;
  const [internalValue, setInternalValue] = useState<string>(value);
  const { autofocus } = style || {};
  const dispatch = useDispatch();

  const internalFns = {
    $setVariable: (key: string, value: any) => {
      setVariableValue(key, value, (localKey, localValue) => {
        dispatch(updateVariableRequest({ variable: localKey, value: localValue }));
      });
    },
    $getVariable: (key: string) => {
      return getVariableValue(key, variableData, localVariableData);
    },
    $moment: (...args: any[]) => moment(...args)
  };

  const handleActionChunk = (actionUpdates: ActionChunk[], filterLoadingState?: StyleUpdateChunk[]) => {
    actionUpdates.forEach((update) => {
      dispatch(actionRequest({ 
        actionUpdate: update, 
        componentId: id 
      }, filterLoadingState));
    });
  };

  const timeoutsRef = useRef<NodeJS.Timeout[]>([]);
  const { 
    on_focus: focusStyles, 
    on_change: changeStyles, 
    on_blur: blurStyles, 
    on_launch: launchUpdates,
    on_hover: hoverUpdates,
    on_unhover: unhoverUpdates,
    on_regexp_success: regexpSuccessUpdates,
    on_regexp_error: regexpErrorUpdates,
    on_outclick: outclickUpdates,
  } = events || {};

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value);
    }
  }, [value]);

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (activeState === 'disabled' || activeState === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    dispatch(submitFormRequest({ block: true }));

    const newValue = e.target.value;

        // Check max length
    if (regexp?.max_length && newValue.length > regexp.max_length) {
      return;
    }
    setInternalValue(newValue);
    dispatch(formValuesRequest({ field: { id: realRef || id, value: newValue } }));
    // Check pattern validation
    if (changeStyles) {
      const styleUpdates = changeStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = changeStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = changeStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = changeStyles.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
        handleActionChunk(actionUpdates, filterLoadingState);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({ 
              location: { pathname: value }, 
              silent,
              componentId: id, 
            }, filterLoadingState));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }, filterLoadingState));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
    if (regexp?.pattern && typeof regexp.pattern === 'string') {
      const result = validateInputPattern(newValue, regexp.pattern, 'gs');
      if (regexpErrorUpdates && !result) {
          const styleUpdates = regexpErrorUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
          const requestUpdates = regexpErrorUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
          const functionUpdates = regexpErrorUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
          const actionUpdates = regexpErrorUpdates.filter((update): update is ActionChunk => update && 'action' in update);
          if (functionUpdates.length) {
            functionUpdates.forEach((update) => {
              if (update?.function) {
                executeFunction(update.function, e, internalFns);
              }
            });
          }
          if (styleUpdates.length) {
            dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
          }
          if (actionUpdates.length) {
            handleActionChunk(actionUpdates);
          }
          if (requestUpdates.length) {
            requestUpdates.forEach((update) => {
              const { method, value, silent, config } = update?.request || {};
              const { delay } = config || {};
              if (update?.request && method === 'command') {
                dispatch(pageRequest({ 
                  location: { pathname: value }, 
                  silent,
                  componentId: id,
                }));
              } else if (update?.request && method === 'action') {
                dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
              } else if (update?.request && method === 'open-url') {
                handleOpenUrl(value, delay, silent, history);
              }
            });
          }

      } else if (regexpSuccessUpdates && result) {
          const styleUpdates = regexpSuccessUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
          const requestUpdates = regexpSuccessUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
          const functionUpdates = regexpSuccessUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
          const actionUpdates = regexpSuccessUpdates.filter((update): update is ActionChunk => update && 'action' in update);
          if (functionUpdates.length) {
            functionUpdates.forEach((update) => {
              if (update?.function) {
                executeFunction(update.function, e, internalFns);
              }
            });
          }
          if (styleUpdates.length) {
            dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
          }
          if (actionUpdates.length) {
            const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
            handleActionChunk(actionUpdates, filterLoadingState);
          }
          if (requestUpdates.length) {
            requestUpdates.forEach((update) => {
              const { method, value, silent, config } = update?.request || {};
              const filterLoadingState = styleUpdates?.filter((update) => update?.active_state === 'loading');
              const { delay } = config || {};
              if (update?.request && method === 'command') {
                dispatch(pageRequest({ 
                  location: { pathname: value }, 
                  silent,
                  componentId: id, 
                }, filterLoadingState));
              } else if (update?.request && method === 'action') {
                dispatch(submitFormRequest({ value, silent, delay, componentId: id }, filterLoadingState));
              } else if (update?.request && method === 'open-url') {
                handleOpenUrl(value, delay, silent, history);
              }
            });
          }
      }
    }
    if (soc) {
      dispatch(submitFormRequest({ value: 'soc', delay, componentId: id }));
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (activeState === 'disabled' || activeState === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (focusStyles) {
      const styleUpdates = focusStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = focusStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = focusStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = focusStyles.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({ 
              location: { pathname: value }, 
              silent,
              componentId: id 
            }));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    if (activeState === 'disabled' || activeState === 'loading') {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (blurStyles) {
      const styleUpdates = blurStyles.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = blurStyles.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = blurStyles.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = blurStyles.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'command') {
            dispatch(pageRequest({ 
              location: { pathname: value }, 
              silent,
              componentId: id, 
            }));
          } else if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  };

  const handleHover = (e: React.MouseEvent<HTMLTextAreaElement>) => {
    if (hoverUpdates) {
      const styleUpdates = hoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = hoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = hoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = hoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }

    }
  
  }

  const handleUnhover = (e:  React.MouseEvent<HTMLTextAreaElement>) => {
    if (unhoverUpdates) {
      const styleUpdates = unhoverUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update);
      const requestUpdates = unhoverUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = unhoverUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = unhoverUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, e, internalFns);
          }
        });
      }
      if (styleUpdates.length) {
        dispatch(mergeStyleRequest(styleUpdates, parentIndex, dataLake?.data));
      }
      if (actionUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length && (activeState !== 'disabled' && activeState !== 'loading')) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }
  }

  useEffect(() => {
    if (launchExecutedRef.current !== style?.visibility && launchUpdates && launchUpdates.length) {
      launchExecutedRef.current = style?.visibility; // Set the flag to true
      const requestUpdates = launchUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update);
      const functionUpdates = launchUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update);
      const actionUpdates = launchUpdates.filter((update): update is ActionChunk => update && 'action' in update);
      if (functionUpdates.length) {
        functionUpdates.forEach((update) => {
          if (update?.function) {
            executeFunction(update.function, undefined, internalFns);
          }
        });
      }
      if (actionUpdates.length) {
        handleActionChunk(actionUpdates);
      }
      if (requestUpdates.length) {
        requestUpdates.forEach((update) => {
          const { method, value, silent, config } = update?.request || {};
          const { delay } = config || {};
          if (update?.request && method === 'action') {
            const newTimeout = setTimeout(() => {
              dispatch(submitFormRequest({ value, silent, delay, componentId: id }));
            }, config?.delay * 1000 || 0);

            timeoutsRef.current.push(newTimeout);
          } else if (update?.request && method === 'open-url') {
            handleOpenUrl(value, delay, silent, history);
          }
        });
      }
    }

    return () => {
      timeoutsRef?.current?.forEach((timeout) => {
        clearTimeout(timeout);
      });
    };
  }, [launchUpdates, style?.visibility]);

  // Memoize the filtered updates to avoid recalculating on every click
  const filteredUpdates = useMemo(() => {
    if (!outclickUpdates) return null;
    
    return {
      styleUpdates: outclickUpdates.filter((update): update is StyleUpdateChunk => update && 'style' in update),
      requestUpdates: outclickUpdates.filter((update): update is RequestUpdateChunk => update && 'request' in update),
      functionUpdates: outclickUpdates.filter((update): update is FunctionUpdateChunk => update && 'function' in update),
      actionUpdates: outclickUpdates.filter((update): update is ActionChunk => update && 'action' in update)
    };
  }, [outclickUpdates]);

  useEffect(() => {
    if (!outclickUpdates?.length) return undefined;
    
    const handleUnclick = (e: MouseEvent) => {
      if (activeState === 'disabled' || activeState === 'loading') return;
      
      if (
        outclickUpdates?.length &&
        inputRef.current && 
        !inputRef.current.contains(e.target as Node) &&
        !isClickInsidePortal(e.target as Node) &&
        filteredUpdates
      ) {
        // Execute function updates
        if (filteredUpdates.functionUpdates.length) {
          filteredUpdates.functionUpdates.forEach((update) => {
            if (update?.function) {
              executeFunction(update.function, undefined, internalFns);
            }
          });
        }
        
        // Apply style updates
        if (filteredUpdates.styleUpdates.length) {
          dispatch(mergeStyleRequest(filteredUpdates.styleUpdates, parentIndex));
        }
        
        // Handle action updates
        if (filteredUpdates.actionUpdates.length) {
          handleActionChunk(filteredUpdates.actionUpdates);
        }
        
        // Process request updates
        if (filteredUpdates.requestUpdates.length) {
          filteredUpdates.requestUpdates.forEach((update) => {
            const { method, value, silent, config } = update?.request || {};
            const { delay } = config || {};
            if (update?.request && method === 'action') {
              dispatch(submitFormRequest({ 
                value, 
                silent, 
                delay, 
                componentId: id 
              }));
            } else if (update?.request && method === 'open-url') {
              handleOpenUrl(value, delay, silent, history);
            }
          });
        }
      }
    };
    
    document.addEventListener('mousedown', handleUnclick);
    return () => {
      document.removeEventListener('mousedown', handleUnclick);
    };
  }, [
    parentIndex, 
    activeState, 
    id, 
    stDraft?.states?.loading,
    outclickUpdates?.length,
  ]);

  if (renderSceleton && style) {
    return <ContainerSkeleton 
      style={style}
      baseScreenResolution={baseScreenResolution}
      skipStates={activeState === 'disabled' || activeState === 'loading'}
    />;
  }

  return (
    <StyledTextarea
      ref={inputRef}
      placeholder={placeholder}
       data-id={realRef || id}
      id={`${realRef || id}${(nestedTable && typeof parentIndex === 'number' && !realRef) ? `::${parentIndex}` : ''}`}
      $shape={style}
      $baseScreenResolution={baseScreenResolution}
      $skipStates={activeState === 'disabled' || activeState === 'loading'}
      data-component="input"
      className={combineClassNames(style.class)}
      value={internalValue}
      title={tooltip}

      autoFocus={autofocus}
      disabled={activeState === 'disabled'}
      onInput={handleInput}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={hoverUpdates?.length ? handleHover : undefined}
      onMouseLeave={unhoverUpdates?.length ? handleUnhover : undefined}
      maxLength={regexp?.max_length}
    />
  );
};

const Email: React.FC<EmailInput> = ({ id, style, placeholder }) => <StyledInput placeholder={placeholder} id={id} type="email" $shape={style} data-component="input" className={combineClassNames(style.class)} />;

const Number: React.FC<NumberInput> = ({ id, style, placeholder }) => <StyledInput placeholder={placeholder} id={id} type="number" $shape={style} data-component="input" className={combineClassNames(style.class)} />;

const File: React.FC<FileInput> = ({ id, style, placeholder }) => <StyledInput placeholder={placeholder} id={id} type="file" $shape={style} data-component="input" className={combineClassNames(style.class)} />;

const Phone: React.FC<PhoneInput> = ({ id, style, placeholder }) => <StyledInput placeholder={placeholder} id={id} type="tel" $shape={style} data-component="input" className={combineClassNames(style.class)} />;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Global: React.FC<TextInput | TextAreaInput | PasswordInput | EmailInput | NumberInput | FileInput | PhoneInput | DateTimePickerInput> = (props) => {
  const { type } = props;

  switch (type) {
    case 'text':
    case 'password':
    case 'number':
      return <Input {...props} />;
    case 'textarea':
      return <TextArea {...props} />;
    // case 'password':
    //   return <Password {...props} />;
    case 'email':
      return <Email {...props} />;
    case 'number':
      return <Number {...props} />;
    case 'file':
      return <File {...props} />;
    case 'phone':
      return <Phone {...props} />;
    case 'daterangepicker':
      return <DateRangePicker {...props} />;
    default:
      return <Input {...props as TextInput} />;
  }
};

export default Global;
